import React, { useCallback, useEffect, useState } from "react";
import { CloudUpload, Download, Upload } from "lucide-react";
import { api, jeton, telecharger } from "./api";
import { BoutonSombre, Eyebrow, Section } from "./ui";
import { useConfirmation } from "./Confirmation";
import Gabarits from "./Gabarits";
import CatalogueStages from "./ReglagesStages";
import ReglagesMenage from "./ReglagesMenage";
import ReglagesGmail from "./ReglagesGmail";
import ReglagesReprise from "./ReglagesReprise";
import ControleImport from "./ControleImport";
import ReglagesSynchro from "./ReglagesSynchro";
import { AnalyseForfaits, ReglesTas } from "./ReglagesTas";
import { Automatismes } from "./ReglagesRegles";
import { Forfaits, Saison } from "./ReglagesForfaits";
import { PassIllimites } from "./ReglagesPass";
import ReglagesPrives from "./ReglagesPrives";

import ReglagesCalendrier from "./ReglagesCalendrier";
import SuiviAide from "./SuiviAide";
import { useChantiers } from "../lib/useChantiers";

/* Réglages de l'école. Le site public lit ces valeurs en direct (tarifs, prix de l'essai,
   portes ouvertes, places de spectacle) : un changement ici s'applique en ligne. */
/* Un interrupteur allumé / éteint, vert quand c'est allumé. */
const Interrupteur = ({ allume, onChange, testid, libelle }) => (
  <button type="button" role="switch" aria-checked={allume} aria-label={libelle} onClick={() => onChange(!allume)}
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-xs font-semibold border transition-colors shrink-0"
    style={allume
      ? { background: "#F0FDF4", borderColor: "#86EFAC", color: "#15803D" }
      : { background: "#F8FAFC", borderColor: "#CBD5E1", color: "#475569" }}>
    <span className="relative w-9 h-5 rounded-full transition-colors" style={{ background: allume ? "#22C55E" : "#CBD5E1" }}>
      <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all" style={{ left: allume ? 18 : 2 }} />
    </span>
    {allume ? "Allumé" : "Éteint"}
  </button>
);

/* Une ligne d'envoi automatique : ce qui part, quand, et l'interrupteur. */
const LigneEnvoi = ({ icone, titre, quand, aide, allume, onChange, testid, children }) => (
  <div className="rounded-2xl border p-4 md:p-5" data-testid={`${testid}-ligne`}
    style={{ borderColor: allume ? "#BBF7D0" : "var(--border)", background: allume ? "#FBFEFC" : "#fff" }}>
    <div className="flex flex-wrap items-start gap-3">
      <span className="text-2xl leading-none mt-0.5" aria-hidden>{icone}</span>
      <div className="min-w-[12rem] flex-1">
        <div className="font-semibold">{titre}</div>
        <div className="text-xs mt-0.5" style={{ color: allume ? "#15803D" : "var(--muted)" }}>
          {allume ? quand : "Éteint : rien ne part tout seul."}
        </div>
        <p className="text-xs text-[var(--muted)] mt-1.5 leading-snug">{aide}</p>
      </div>
      <Interrupteur allume={allume} onChange={onChange} testid={testid} libelle={titre} />
    </div>
    {children}
  </div>
);

/* « Envois automatiques » (simplifié le 12.10.2026, « simplifie ») : les envois qui
   partent tout seuls chaque matin, chacun avec son interrupteur (le rappel des cours d'essai
   la veille depuis le 31.10.2026). On les garde : c'est le
   frein d'urgence de l'école (un modèle d'e-mail faux, une période particulière) — un clic,
   sans attendre une mise en ligne. Éteindre demande une confirmation. La date de démarrage
   des attestations (« à partir du ») a disparu : elle ne servait qu'au premier semestre. */
const EnvoisAutomatiques = ({ data, enregistrer, informer, demander }) => {
  const v = data.valeurs || {};
  const attestations = !!v.attestations_envoi_auto;
  const anniversaires = !!v.anniversaires_envoi_auto;
  const rappelEssai = v.essais_rappel_veille !== false;
  const avisEssai = v.essais_avis_apres !== false;
  const placeLiberee = v.ecole_place_liberee !== false;
  const chantiers = useChantiers();
  const basculer = async (nom, cle, allumer, messages) => {
    if (!allumer && !await demander({
      titre: `Éteindre : ${nom}`,
      texte: "Plus rien ne partira tout seul. Tu pourras le rallumer d'un clic.",
      valider: "Éteindre",
      annuler: "Laisser allumé",
    })) return;
    // Rallumer efface aussi l'ancienne date de démarrage des attestations.
    await enregistrer(cle === "attestations_envoi_auto"
      ? { attestations_envoi_auto: allumer, ...(allumer ? { attestations_envoi_auto_des: "" } : {}) }
      : { [cle]: allumer }, allumer ? messages[0] : messages[1]);
  };
  return (
    <div className="space-y-3">
      <LigneEnvoi icone="🛡️" titre="Attestations d'assurance" testid="champ-attestations-auto"
        quand="Allumé : chaque matin à 6h15"
        aide="Les attestations dont la facture est payée et le n° AVS connu partent seules ; les n° AVS manquants sont réclamés aux familles."
        allume={attestations}
        onChange={(oui) => basculer("attestations", "attestations_envoi_auto", oui,
          ["Attestations : envoi automatique allumé (chaque matin à 6h15).",
            "Attestations : envoi automatique éteint — plus rien ne part sans ton clic."])} />
      <LigneEnvoi icone="🎂" titre="Anniversaires" testid="champ-anniversaires-auto"
        quand="Allumé : chaque matin à 6h00"
        aide="Le jour de son anniversaire, chaque élève inscrit cette saison reçoit « Joyeux anniversaire » à l'adresse de sa famille (une fois par an). Allumé dans la journée : ceux du jour partent tout de suite. Le texte se modifie dans les modèles d'e-mails ci-dessous."
        allume={anniversaires}
        onChange={(oui) => basculer("anniversaires", "anniversaires_envoi_auto", oui,
          ["Anniversaires allumés : les e-mails partent chaque matin à 6h00.",
            "Anniversaires éteints : plus aucun e-mail d'anniversaire ne part."])}>
        <ProchainsAnniversaires informer={informer} />
      </LigneEnvoi>
      <LigneEnvoi icone="⏰" titre="Rappel des cours d'essai" testid="champ-rappel-essai"
        quand="Allumé : la veille, à 10h00"
        aide="La veille d'un cours d'essai, la famille reçoit un rappel (même style que la confirmation) : le cours, l'heure, la salle, le prix, l'adresse avec l'itinéraire, ce qu'il faut prévoir et « Changer la date ou annuler ». Un e-mail par famille, une fois par essai ; un essai annulé ou déplacé est respecté."
        allume={rappelEssai}
        onChange={(oui) => basculer("rappel des cours d'essai", "essais_rappel_veille", oui,
          ["Rappel des cours d'essai allumé : les familles le reçoivent la veille à 10h00.",
            "Rappel des cours d'essai éteint : plus aucun rappel ne part."])} />
      <LigneEnvoi icone="🔔" titre="Prévenir l'école quand une place se libère" testid="champ-place-liberee"
        quand="Allumé : contrôlé toutes les 15 minutes"
        aide="Un cours où des élèves attendent retrouve une place : tu reçois un e-mail et une notification sur le téléphone, une fois par place, avec qui attend (du plus ancien au plus récent). C'est toi qui choisis à qui la donner : aucune famille n'est prévenue toute seule."
        allume={placeLiberee}
        onChange={(oui) => basculer("prévenir l'école quand une place se libère", "ecole_place_liberee", oui,
          ["Allumé : l'école est prévenue dès qu'une place se libère dans un cours avec liste d'attente.",
            "Éteint : plus d'e-mail ni de notification quand une place se libère."])} />
      <RappelsInstallation informer={informer} demander={demander} />
      <RappelDesinscription informer={informer} demander={demander} />
      {chantiers.avis_essais && (
        <LigneEnvoi icone="⭐" titre="Avis après le cours d'essai" testid="champ-avis-essai"
          quand="Allumé : environ 1 h 30 après le cours (entre 9h et 21h)"
          aide="Un e-mail très court : « Comment s'est passé ton cours d'essai ? » et cinq étoiles (un clic = la note). Puis « Envie de continuer ? » : Oui (bouton pour s'inscrire au cours), J'hésite ou Non (pourquoi : horaire, tarif, niveau, ambiance, distance, un autre cours, autre ; « rappelez-moi »). Les « J'hésite », « Non », « rappelez-moi » et les mots arrivent dans Messages reçus. Pas d'e-mail pour un élève absent, un essai annulé ou déjà inscrit au cours. En test : seulement les familles fictives."
          allume={avisEssai}
          onChange={(oui) => basculer("avis après le cours d'essai", "essais_avis_apres", oui,
            ["Avis après le cours d'essai allumé.", "Avis après le cours d'essai éteint : plus aucun e-mail d'avis ne part."])}>
          <AvisEssais informer={informer} />
        </LigneEnvoi>
      )}
    </div>
  );
};

/* Les chiffres de l'avis après l'essai (90 derniers jours) et « M'envoyer un exemple ». */
const AvisEssais = ({ informer }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api("/essais-avis").then(setD).catch(() => {}); }, []);
  const exemple = async () => {
    try {
      const r = await api("/essais-avis/exemple", { method: "POST" });
      informer({ titre: r.message, texte: "Ouvre l'e-mail et touche une étoile : la page de l'avis s'ouvre avec la note déjà prise." });
    } catch (e) { informer({ titre: "Exemple impossible", ton: "danger", texte: e.message }); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="avis-essai-chiffres">
      {d && (d.reponses ? (
        <>
          <span><strong className="text-sm">{d.note_moyenne ?? "—"}</strong> ★ en moyenne</span>
          <span><strong className="text-sm">{d.reponses}</strong> réponses sur {d.envoyes} ({d.taux} %)</span>
          <span>Oui <strong>{d.oui}</strong> · J'hésite <strong>{d.hesite}</strong> · Non <strong>{d.non}</strong></span>
          {d.raisons.length > 0 && <span className="text-[var(--muted)]">{d.raisons.slice(0, 3).map(([r, n]) => `${r} ${n}`).join(" · ")}</span>}
        </>
      ) : <span className="text-[var(--muted)]">Pas encore de réponse ces 90 derniers jours.</span>)}
      <button className="btn-ghost text-xs ml-auto" onClick={exemple} data-testid="avis-essai-exemple">M'envoyer un exemple</button>
    </div>
  );
};

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août",
  "septembre", "octobre", "novembre", "décembre"];
const moisLisible = (m) => { const [a, mm] = String(m || "").split("-").map(Number); return mm ? `${MOIS[mm - 1]} ${a}` : ""; };
const decalerMois = (m, n) => {
  const [a, mm] = m.split("-").map(Number);
  const d = new Date(a, mm - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/* Le premier mois des heures des profs dans leur application (école, 21.10.2026 :
   « septembre a été fait à la main, on passe à l'app dès octobre »). Un réglage de mise en
   route, réglé une fois : il se lit d'un coup d'œil ici, dans Saison et calendrier, et ne
   se change qu'avec « Modifier » puis une confirmation — un mauvais mois ferait réclamer
   aux profs des heures déjà payées à la main, ou en cacherait. */
const HeuresProfsDepuis = ({ des, enregistrer, demander }) => {
  const [edition, setEdition] = useState(false);
  const [choix, setChoix] = useState(des);
  useEffect(() => { setChoix(des); }, [des]);
  if (!des) return null;
  const valider = async () => {
    if (choix === des) { setEdition(false); return; }
    const plusTot = choix < des;
    if (!await demander({
      titre: `Heures des profs dans l'application depuis ${moisLisible(choix)} ?`,
      texte: plusTot
        ? `Les profs verront à cocher et à envoyer dans leur application leurs heures depuis ${moisLisible(choix)}, et les rappels du 1er et du 5 du mois les leur réclameront.`
        : `Les heures d'avant ${moisLisible(choix)} ne seront plus demandées dans l'application (faites à la main) : plus rien à cocher ni à envoyer pour ces mois, aucun rappel.`,
      valider: "Changer",
      annuler: "Ne rien changer",
    })) return;
    await enregistrer({ profs_heures_app_des: choix }, `Heures des profs dans l'application depuis ${moisLisible(choix)}.`);
    setEdition(false);
  };
  return (
    <div className="text-sm space-y-2" data-testid="reglages-heures-profs-depuis">
      {!edition ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p>
            Depuis <strong data-testid="reglages-heures-profs-mois">{moisLisible(des)}</strong>. Les mois d'avant ont
            été décomptés à la main (ancien système) : l'application ne les demande pas aux profs et aucun rappel ne part.
          </p>
          <button type="button" className="btn-ghost text-xs" onClick={() => setEdition(true)} data-testid="reglages-heures-profs-modifier">
            Modifier
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select className="input-field py-1 text-sm" style={{ width: "auto" }} value={choix}
            onChange={(e) => setChoix(e.target.value)} data-testid="reglages-heures-profs-choix">
            {[-4, -3, -2, -1, 0, 1, 2, 3].map((n) => decalerMois(des, n)).map((m) => (
              <option key={m} value={m}>{moisLisible(m)}</option>
            ))}
          </select>
          <button type="button" className="btn-primary text-xs" onClick={valider} data-testid="reglages-heures-profs-valider">Enregistrer</button>
          <button type="button" className="btn-ghost text-xs" onClick={() => { setChoix(des); setEdition(false); }}>Annuler</button>
        </div>
      )}
    </div>
  );
};

/* « Installe l'application » (Jen, 31.10.2026 : avec les autres envois automatiques, plus
   dans le menu Application) : un mois après le dernier e-mail de l'app reçu (le mode d'emploi
   du 21.10 → un rappel le 21.11), puis chaque mois tant que l'app n'est pas installée ; au
   plus N par matin. Son réglage vit à part (/rappels-app). */
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const dateCourte = (iso) => { const d = new Date(`${iso}T12:00:00`); return `${d.getDate()} ${MOIS_COURTS[d.getMonth()]}`; };
/* Le rappel de la date limite de désinscription, 15 jours avant chacune (Jen, 05.11.2026 :
   « l'automatiser 15 jours avant la deadline, chaque semestre, une fois que je valide le
   template »). Éteint au départ ; le texte est le modèle d'Envois groupés. */
const RappelDesinscription = ({ informer, demander }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api("/envois/rappel-desinscription").then(setD).catch(() => {}); }, []);
  if (!d) return null;
  const basculer = async (oui) => {
    if (oui && !await demander({
      titre: "Allumer : rappel de la date limite de désinscription",
      texte: `Le ${d.prochain_envoi_texte}, toutes les familles de la saison recevront le modèle « Rappel : date limite de désinscription » (date limite : ${d.date_limite_texte}), puis 15 jours avant chaque date limite. Relis le modèle avant (« M'envoyer un exemple »).`,
      valider: "Allumer", annuler: "Pas encore",
    })) return;
    if (!oui && !await demander({ titre: "Éteindre : rappel de désinscription", texte: "Plus aucun rappel ne partira tout seul. Tu pourras le rallumer d'un clic.",
      valider: "Éteindre", annuler: "Laisser allumé" })) return;
    try {
      const x = await api("/envois/rappel-desinscription", { method: "PUT", body: JSON.stringify({ actif: oui }) });
      setD(x);
      informer({ titre: oui ? `Rappel de désinscription allumé : prochain envoi le ${x.prochain_envoi_texte}.` : "Rappel de désinscription éteint." });
    } catch (e) { informer({ titre: "Réglage impossible", ton: "danger", texte: e.message }); }
  };
  const exemple = async () => {
    try {
      const r = await api("/envois/rappel-desinscription/exemple", { method: "POST" });
      informer({ titre: r.message, texte: "L'e-mail tel que les familles le recevront, avec la prochaine date limite." });
    } catch (e) { informer({ titre: "Exemple impossible", ton: "danger", texte: e.message }); }
  };
  return (
    <LigneEnvoi icone="📅" titre="Rappel de la date limite de désinscription" testid="rappel-desinscription-interrupteur"
      quand={`Allumé : ${d.jours_avant} jours avant chaque date limite, à 10h05`}
      aide="Toutes les familles de la saison (sauf celles déjà entièrement désinscrites) reçoivent le modèle « Rappel : date limite de désinscription » d'Envois groupés, aussi dans l'app. Le texte se modifie dans Messages › Envois groupés (choisir le modèle, retoucher, « Enregistrer comme modèle ») ; {date_limite} devient la date."
      allume={d.actif} onChange={basculer}>
      <div className="mt-4 pt-3 border-t text-xs text-[var(--muted)] flex flex-wrap items-center gap-x-4 gap-y-2" style={{ borderColor: "var(--border-soft)" }} data-testid="rappel-desinscription">
        <span>Prochaine date limite : <strong className="text-[var(--ink)]">{d.date_limite_texte}</strong> · {d.actif ? "envoi" : "envoi s'il est allumé"} le <strong className="text-[var(--ink)]">{d.prochain_envoi_texte}</strong></span>
        {d.dernier && <span>Dernier envoi : {String(d.dernier.le || "").slice(0, 10).split("-").reverse().join(".")} · {d.dernier.a_envoyer} famille{d.dernier.a_envoyer > 1 ? "s" : ""}</span>}
        <button className="btn-ghost text-xs ml-auto" onClick={exemple} data-testid="rappel-desinscription-exemple">M'envoyer un exemple</button>
      </div>
    </LigneEnvoi>
  );
};

const RappelsInstallation = ({ informer, demander }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api("/rappels-app").then(setD).catch(() => {});
  }, []);
  if (!d) return null;
  const regler = async (actif, message) => {
    try {
      const x = await api("/rappels-app", { method: "PUT", body: JSON.stringify({ actif }) });
      setD(x);
      if (message) informer({ titre: message });
    } catch (e) {
      informer({ titre: "Réglage impossible", ton: "danger", texte: e.message });
    }
  };
  const basculer = async (oui) => {
    if (!oui && !await demander({ titre: "Éteindre : rappels d'installation", texte: "Plus aucun rappel ne partira tout seul. Tu pourras le rallumer d'un clic.",
      valider: "Éteindre", annuler: "Laisser allumé" })) return;
    await regler(oui, oui ? "Rappels d'installation allumés : chaque matin à 10h00." : "Rappels d'installation éteints.");
  };
  return (
    <LigneEnvoi icone="📱" titre="Rappel « installe l'application »" testid="rappels-app-interrupteur"
      quand="Allumé : chaque matin à 10h00"
      aide="Les familles de la saison qui n'ont pas installé l'app reçoivent le mode d'emploi de l'app un mois après le dernier e-mail de l'app reçu (l'envoi du 21 octobre → un rappel le 21 novembre), puis chaque mois tant qu'elles ne l'ont pas installée. Une famille qui n'a jamais rien reçu : un mois après son inscription."
      allume={d.actif} onChange={basculer}>
      <div className="mt-4 pt-3 border-t text-xs text-[var(--muted)] space-y-1.5" style={{ borderColor: "var(--border-soft)" }} data-testid="rappels-app">
        <div>
          <strong className="text-[var(--ink)]">{d.sans_app}</strong> famille{d.sans_app > 1 ? "s" : ""} sans l'app ·{" "}
          {d.dus_aujourdhui ? <>aujourd'hui, <strong className="text-[var(--ink)]">{d.dus_aujourdhui}</strong> rappel{d.dus_aujourdhui > 1 ? "s" : ""} dû{d.dus_aujourdhui > 1 ? "s" : ""}</> : "aucun rappel dû aujourd'hui"}
          {d.envoyes ? ` · ${d.envoyes} déjà envoyé${d.envoyes > 1 ? "s" : ""}` : ""}
        </div>
        {d.a_venir.length > 0 && (
          <div>Ensuite : {d.a_venir.map((v) => `${dateCourte(v.date)} — ${v.familles} famille${v.familles > 1 ? "s" : ""}`).join(" · ")}</div>
        )}
      </div>
    </LigneEnvoi>
  );
};

/* Sous la ligne « Anniversaires » : ceux de la semaine, et l'exemple dans sa propre boîte. */
const ProchainsAnniversaires = ({ informer }) => {
  const [prochains, setProchains] = useState(null);
  useEffect(() => {
    api("/anniversaires").then(setProchains).catch(() => setProchains({ jours: [] }));
  }, []);
  const [envoi, setEnvoi] = useState(false);
  // Le vrai e-mail, par le vrai chemin, dans sa propre boîte (un exemple composé dans
  // Gmail perd son animation).
  const exemple = async () => {
    setEnvoi(true);
    try {
      const r = await api("/anniversaires/exemple", { method: "POST" });
      informer(r.simule
        ? { titre: "Rien n'est parti", texte: "La console est en mode test : aucun e-mail ne part." }
        : { titre: "Exemple envoyé", texte: `Regarde la boîte de ${r.a} : l'e-mail est celui que recevront les familles, avec le prénom Léa.` });
    } catch (e) {
      informer({ titre: "L'exemple n'est pas parti", ton: "danger", texte: e.message || "Réessaie dans un instant." });
    } finally {
      setEnvoi(false);
    }
  };
  const jour = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CH",
    { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="mt-4 pt-3 border-t flex flex-wrap items-end gap-4" style={{ borderColor: "var(--border-soft)" }}>
      <div className="text-xs text-[var(--muted)] min-w-[14rem] flex-1" data-testid="anniversaires-semaine">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-1">Les sept prochains jours</div>
        {prochains === null ? "Chargement…" : prochains.jours.length ? (
          <ul className="space-y-1">
            {prochains.jours.map((j) => (
              <li key={j.le}>
                <strong className="text-[var(--ink)]">{jour(j.le)}</strong> :{" "}
                {j.eleves.map((e) => `${e.eleve} (${e.age} ans)${e.email ? "" : " — pas d'adresse e-mail"}`).join(", ")}
              </li>
            ))}
          </ul>
        ) : "Aucun anniversaire d'élève inscrit."}
      </div>
      <button type="button" className="text-xs font-semibold underline" style={{ color: "#0E7490" }}
        data-testid="bouton-anniversaire-exemple" onClick={exemple} disabled={envoi}>
        {envoi ? "Envoi…" : "M'envoyer un exemple"}
      </button>
    </div>
  );
};

/* Les réglages étaient une seule page très longue : ils sont rangés en six rubriques,
   choisies en haut (la dernière ouverte est gardée). */
const GROUPES_REGLAGES = [{ id: "saison", label: "Saison et calendrier", aide: "Dates de la saison, vacances, portes ouvertes, début des heures des profs dans l'app" }, { id: "tarifs", label: "Tarifs et forfaits", aide: "Prix, prorata, forfaits, pass, réductions, encaissement" }, { id: "stages", label: "Stages", aide: "Catalogue des stages de vacances" }, { id: "prives", label: "Cours privés", chantier: "cours_prives", aide: "Réservation, disciplines et prix, annulation et absence, salaire du prof, rappels des factures : les règles des cours privés (Jen, 02.11.2026)" }, { id: "documents", label: "Attestations et e-mails", aide: "Codes TAS, envois automatiques, modèles, copies" }, { id: "outils", label: "Outils", aide: "Automatismes, sauvegarde, impressions, inscriptions du site, ménage" }, { id: "suivi-aide", label: "Suivi de l'Aide", aide: "Ce que l'équipe demande à l'Aide de la console (bouton en bas à droite) : qui, quand, sur quel écran, et ce que le guide ne savait pas expliquer." }];

export default function Reglages() {
  const { demander, informer } = useConfirmation();
  const chantiers = useChantiers();
  const groupes = GROUPES_REGLAGES.filter((g) => !g.chantier || chantiers[g.chantier]);
  const [groupe, setGroupe] = useState(() => {
    // Retour de Google Drive : on rouvre directement la sauvegarde automatique.
    if (retourDrive()) return "outils";
    try { return sessionStorage.getItem("reglages_groupe") || "saison"; } catch { return "saison"; }
  });
  const choisir = (g) => {
    setGroupe(g);
    try { sessionStorage.setItem("reglages_groupe", g); } catch { /* navigation privée */ }
    window.scrollTo({ top: 0 });
  };
  const [data, setData] = useState(null);
  const [info, setInfo] = useState("");
  const [erreur, setErreur] = useState("");

  const charger = useCallback(async () => {
    try { setData(await api("/reglages")); } catch (e) { setErreur(e.message); }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  // Ouvert depuis un autre écran sur une section précise (Profs → réglage des heures) : on
  // y descend une fois les réglages affichés.
  useEffect(() => {
    if (!data) return;
    let ancre = "";
    try { ancre = sessionStorage.getItem("reglages_ancre") || ""; sessionStorage.removeItem("reglages_ancre"); } catch { /* rien */ }
    if (ancre) setTimeout(() => document.getElementById(ancre)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [data]);

  const enregistrer = async (valeurs, message) => {
    setErreur(""); setInfo("");
    try {
      await api("/reglages", { method: "PATCH", body: JSON.stringify({ valeurs }) });
      setInfo(message || "Réglage enregistré.");
      await charger();
    } catch (e) { setErreur(e.message); }
  };



  if (erreur && !data) return <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="reglages-erreur">{erreur}</p>;
  if (!data) return <p className="text-sm text-[var(--muted)]">Chargement des réglages…</p>;

  const v = data.valeurs;

  return (
    <div className="space-y-8" data-testid="reglages">
      {info && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="reglages-info">{info}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="reglages-erreur">{erreur}</div>}

      <nav className="sticky top-[60px] z-20 -mx-1 px-1 py-2 flex flex-wrap gap-2" style={{ background: "#F4F7F9" }}
        data-testid="reglages-sommaire">
        {groupes.map((g) => (
          <button key={g.id} onClick={() => choisir(g.id)} data-testid={`reglages-groupe-${g.id}`}
            title={g.aide}
            className="px-4 py-2 rounded-full text-sm transition-colors"
            style={groupe === g.id
              ? { background: "#0F1B2D", color: "#fff", fontWeight: 600 }
              : { background: "#fff", color: "var(--text)", border: "1px solid var(--border)" }}>
            {g.label}
          </button>
        ))}
      </nav>
      <p className="text-xs text-[var(--muted)] -mt-4">{GROUPES_REGLAGES.find((g) => g.id === groupe)?.aide}</p>

      {groupe === "saison" && (
        <>
        <Section titre="Saison" aide="Ce que chaque date décide, écrit sous le champ." testid="reglages-saison">
          <Saison
            saison={v.saison}
            semestreCourant={data.semestre_courant}
            onEnregistrer={(s) => enregistrer({ saison: { ...v.saison, ...s } }, "Dates de saison enregistrées.")}
          />
          <p className="text-[11px] text-[var(--muted)]">
            Changer une date ici ne touche pas l'historique : les factures déjà envoyées gardent leur
            montant et leur échéance. Les nouvelles dates servent aux prochains calculs (prorata,
            fermeture des inscriptions, semestre courant).
          </p>
          <p className="text-[11px] text-[var(--muted)]">
            Les fenêtres de portes ouvertes se règlent dans <strong>Calendrier des séances</strong>,
            juste en dessous. Le site public lit ces réglages <strong>dans la console</strong> :
            portes ouvertes, prix du cours d'essai et bascule spectacle s'appliquent en ligne dans
            la minute.
          </p>
        </Section>
        <ReglagesCalendrier valeurs={data.valeurs} onEnregistre={charger} />
        <div id="heures-profs">
          <Section titre="Heures des profs dans l'application" testid="reglages-heures-profs"
            aide="Réglage de mise en route : le premier mois que les profs cochent et envoient dans leur application.">
            <HeuresProfsDepuis des={v.profs_heures_app_des} enregistrer={enregistrer} demander={demander} />
          </Section>
        </div>
        </>
      )}

      {groupe === "tarifs" && (
        <>
        <Section titre="Tarifs du semestre" aide="Prix par durée de cours. Appliqués par la console et lus en direct par le site : un prix changé ici s'affiche en ligne dans la minute." testid="reglages-tarifs">
          <div className="grid md:grid-cols-2 gap-3">
            <TableNombres
              titre="Enfants et ados"
              valeurs={v.tarifs_enfants}
              suffixe="CHF"
              legende="min"
              testid="tarifs-enfants"
              onEnregistrer={(t) => enregistrer({ tarifs_enfants: t }, "Tarifs enfants enregistrés.")}
            />
            <TableNombres
              titre="Adultes (dès 18 ans)"
              valeurs={v.tarifs_adultes}
              suffixe="CHF"
              legende="min"
              testid="tarifs-adultes"
              onEnregistrer={(t) => enregistrer({ tarifs_adultes: t }, "Tarifs adultes enregistrés.")}
            />
          </div>
        </Section>
        {/* Le prorata change le montant des factures : il vit avec les tarifs (école, 09.10.2026). */}
        <Section titre="Prorata d'entrée en cours de semestre"
          aide="Compté au mois, jamais à la séance. Le semestre vaut 5 mois. Les mois déjà passés ne sont pas facturés ; le mois de l'inscription compte pour moitié si elle tombe à partir du jour réglé ici (décision de l'école, 24.09.2026)."
          testid="reglages-prorata">
          <Prorata
            jour={(v.prorata || {}).jour_bascule || 15}
            semestres={v.semestres}
            onEnregistrer={(n) => enregistrer({ prorata: { ...(v.prorata || {}), jour_bascule: n } },
              `Prorata : le mois de l'inscription compte pour moitié dès le ${n} du mois.`)}
          />
        </Section>
        <Section titre="Forfaits" aide="Un forfait remplace le tarif à la durée et sort le cours de la réduction d'accumulation. Modifiable ici : prix, costume, cours rattachés." testid="reglages-forfaits">
          <Forfaits
            forfaits={v.forfaits}
            cours={data.cours}
            onCreer={async (f) => {
              setErreur(""); setInfo("");
              try {
                await api("/reglages/forfaits", { method: "POST", body: JSON.stringify(f) });
                setInfo(`Forfait « ${f.nom} » créé sur ${f.cours.length} cours.`);
                await charger();
                return true;
              } catch (e) { setErreur(e.message); return false; }
            }}
            onModifier={async (nom, f) => {
              setErreur(""); setInfo("");
              try {
                await api(`/reglages/forfaits/${encodeURIComponent(nom)}`, { method: "PATCH", body: JSON.stringify(f) });
                setInfo(`Forfait « ${f.nom} » enregistré.`);
                await charger();
                return true;
              } catch (e) { setErreur(e.message); return false; }
            }}
            onSupprimer={async (nom) => {
              setErreur(""); setInfo("");
              try {
                const r = await api(`/reglages/forfaits/${encodeURIComponent(nom)}`, { method: "DELETE" });
                setInfo(`Forfait « ${nom} » supprimé : ${r.cours_liberes.length} cours repassent au tarif à la durée.`);
                await charger();
              } catch (e) { setErreur(e.message); }
            }}
          />
          <AnalyseForfaits analyse={data.forfaits_analyse} />
        </Section>
        <PassIllimites valeurs={data.valeurs} onEnregistre={charger} />
        <Section titre="Paramètres et règles" aide="Taxe, réductions, costumes, essai, parrainage, seuil « dernières places », délais des rappels de paiement." testid="reglages-parametres">
          <TableNombres
            titre="Montants"
            valeurs={v.parametres}
            suffixe="CHF"
            libelles={{
              taxe_admin_premiere_inscription: "Taxe administrative (1re inscription)",
              reduction_2eme_enfant: "Réduction 2e enfant",
              reduction_3eme_enfant: "Réduction 3e enfant",
              reduction_4eme_enfant: "Réduction 4e enfant et suivants",
              prix_cours_essai: "Cours d'essai hors portes ouvertes",
              costume_enfant: "Costume enfant (2 spectacles)",
              costume_adulte: "Costume adulte (1 spectacle)",
              parrainage: "Bon de parrainage",
              parrainage_meme_famille: "Bon de parrainage dans la même famille (quand elle a déjà la réduction famille)",
              parrainages_max_par_semestre: "Parrainages max par semestre",
              // Les marches s'additionnent : 3 cours = 2e + 3e (50 + 100 = 150 par défaut).
              reduction_accumulation_2e_cours: "Réduction d'accumulation — sur le 2e cours",
              reduction_accumulation_3e_cours: "Réduction d'accumulation — sur le 3e cours (s'ajoute à celle du 2e : 3 cours = 2e + 3e)",
              reduction_accumulation_cours_suivant: "Réduction d'accumulation — sur chaque cours dès le 4e (s'ajoute aux précédentes)",
            }}
            testid="parametres"
            onEnregistrer={(t) => enregistrer({ parametres: t }, "Paramètres enregistrés.")}
          />
          <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }}>
            <Eyebrow>Seuil « dernières places »</Eyebrow>
            <SeuilPlaces valeur={v.seuil_dernieres_places} onEnregistrer={(n) => enregistrer({ seuil_dernieres_places: n }, "Seuil enregistré.")} />
          </div>
          {/* Les deux délais qui décident QUI reçoit un rappel de paiement (Facturation,
              À traiter, mode école) : la console les applique seule (09.10.2026). */}
          <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }}
            data-testid="reglages-relances">
            <Eyebrow>Rappels de paiement</Eyebrow>
            <DelaisRelances paiement={v.delai_paiement_jours} entre={v.delai_entre_relances_jours}
              onEnregistrer={(champs) => enregistrer(champs, "Délais des rappels enregistrés.")} />
          </div>
        </Section>
        <Section titre="Encaissement — bulletin de Noémie"
          aide="Les cours de ces disciplines sont encaissés par Noémie Vaney : dès qu'un seul apparaît sur la facture d'une famille, tout le bulletin est établi à son nom."
          testid="reglages-bv-noemie">
          <BvNoemie
            disciplines={v.disciplines_bv_noemie || []}
            toutes={[...new Set((data.cours || []).map((c) => c.discipline).filter(Boolean))].sort((a, b) => a.localeCompare(b))}
            stages={!!v.stages_bv_noemie}
            onEnregistrer={(champs, message) => enregistrer(champs, message)}
          />
        </Section>
        </>
      )}

      {groupe === "stages" && (
        <>
        <CatalogueStages valeurs={data.valeurs} onEnregistre={charger} />
        </>
      )}

      {/* Les règles des cours privés (Jen, 02.11.2026 : « tout ça dans les réglages, pour que
          je puisse repasser au travers avec l'école ») : leur propre écran, façon iPhone. */}
      {groupe === "prives" && groupes.some((g) => g.id === "prives") && <ReglagesPrives demander={demander} />}

      {groupe === "documents" && (
        <>
        <Section titre="Codes d'assurance TAS par groupe de cours"
          aide="Le seul endroit où se règle le code reporté sur l'attestation tarif 595 : tous les groupes de cours y figurent, y compris ceux qui n'ont pas encore de code. (Il a été retiré de la fiche cours du planning.)"
          testid="reglages-tas-table">
          <TableTexte
            valeurs={v.codes_tas}
            placeholder="3021"
            testid="codes-tas"
            onEnregistrer={(t) => enregistrer({ codes_tas: t }, "Codes TAS enregistrés.")}
          />
          <ReglesTas tas={data.tas} />
        </Section>
        <Section titre="Envois automatiques" testid="reglages-envois-automatiques"
          aide="Ce qui part tout seul chaque matin. Garde-les allumés ; en cas de souci (un e-mail faux, une période particulière), un clic les arrête.">
          <EnvoisAutomatiques data={data} enregistrer={enregistrer} informer={informer} demander={demander} />
        </Section>
        <Gabarits />
        {/* Visible par l'école : elle doit pouvoir décider qui reçoit une copie des factures.
            Seule la bascule test ↔ réel reste réservée au mode avancé, à l'intérieur. */}
        <Section titre="Copie des e-mails à l'école" testid="reglages-emails"
          aide="Qui reçoit une copie de ce qui part aux familles. Rien ne part jamais tout seul : un e-mail ne quitte la console que sur un clic.">
          <ModeEmails reel={data.emails_mode_reel} copie={data.email_copie_ecole}
            peutChanger={data.peut_changer_mode_emails !== false}
            onChange={charger} demander={demander} informer={informer} />
        </Section>
        {/* Gmail → Messages reçus « Traité » (02.11.2026). */}
        <div id="gmail">
          <Section titre="Gmail : messages répondus" testid="reglages-gmail-section"
            aide="Une réponse envoyée depuis Gmail marque le message « Traité » dans Messages reçus ; une relance de la famille sans réponse le remet « À traiter ».">
            <ReglagesGmail demander={demander} />
          </Section>
        </div>
        </>
      )}

      {groupe === "suivi-aide" && <SuiviAide />}

      {groupe === "outils" && (
        <>
        <Automatismes automatismes={data.automatismes} manuel={data.manuel} />
        <Section titre="Manuel de la console" testid="reglages-documents"
          aide="Le mode d'emploi complet de la console, en PDF. Les feuilles de présence s'impriment depuis Liste.">
          <Manuel informer={informer} />
        </Section>
        <div id="sauvegarde">
          <Section titre="Sauvegarde automatique" testid="reglages-sauvegarde-auto"
            aide="Chaque nuit, une copie chiffrée de toute la base part sur le Google Drive de l'école. Gardées : les 30 derniers jours et une par mois sur 12 mois.">
            <SauvegardeAuto informer={informer} />
          </Section>
        </div>
        <Section titre="Sauvegarde de la console" testid="reglages-sauvegarde"
          aide="Un fichier avec tout ce que contient la console : élèves, familles, inscriptions, facturation, réglages. À garder de côté de temps en temps, en lieu sûr.">
          <Sauvegarde informer={informer} demander={demander} />
        </Section>
        {/* Après « Importer une sauvegarde » : le fichier comparé à la console (soir de la
            bascule, demandé par l'école le 09.10.2026). */}
        <div id="controle-import">
          <Section titre="Contrôle de l'import" testid="reglages-controle-import"
            aide="Après chaque « Importer une sauvegarde » : ce que contient le fichier, comparé à ce que la console contient maintenant. Rouge = quelque chose manque, avec qui.">
            <ControleImport />
          </Section>
        </div>
        {/* Servait le soir de la bascule : rejouer un ancien export écraserait des réglages
            changés depuis en production. Mode avancé seulement (audit du 10.10.2026). */}
        {modeAvance() && (
          <Section titre="Reprise de la configuration (test → production)" testid="reglages-reprise"
            aide="Le soir de la bascule : ce qui a été réglé seulement sur la version de test (fiches des profs, e-mails types, assistant, réglages des cours…) repris dans la production, après l'import d'Emergent. Jamais de données de familles.">
            <ReglagesReprise />
          </Section>
        )}
        <Section titre="Inscriptions du site" testid="reglages-synchro"
          aide="Tout ce que les familles envoient depuis le site arrive ici automatiquement, dans la minute.">
          <ReglagesSynchro />
        </Section>
        {/* « Déposer une facture refaite (PDF) » a été retiré le 10.10.2026 (demandé par
            l'école) : il servait à reprendre les factures d'Emergent. La console refait
            maintenant elle-même une facture corrigée, et chaque ligne de Facturation ouvre
            le PDF reçu par la famille. */}
        <Section titre="Supprimer les lignes de test" testid="reglages-menage"
          aide="Efface les fausses inscriptions et les faux messages laissés par les tests, sans toucher aux vraies familles. Tu vois tout avant de supprimer.">
          <ReglagesMenage />
        </Section>
        </>
      )}

    </div>
  );
}

/* Deux commandes peuvent casser la production d'un clic (revenir au classeur, repasser en
   mode test). La console est partagée avec l'équipe : elles ne s'affichent que si on ajoute
   « ?avance=1 » à l'adresse. L'état, lui, reste toujours visible. */
const modeAvance = () =>
  new URLSearchParams(window.location.search).get("avance") === "1";

/* Google renvoie sur /console?onglet=reglages&drive=ok|erreur|refus. La console s'ouvre sur
   le tableau de bord : on la ramène ici (plusieurs essais, le temps qu'elle soit montée). */
const retourDrive = () => {
  try {
    return window.location.pathname.startsWith("/console")
      && new URLSearchParams(window.location.search).get("drive");
  } catch { return ""; }
};
if (typeof window !== "undefined" && retourDrive()) {
  [0, 300, 1000].forEach((ms) => setTimeout(() => window.dispatchEvent(
    new CustomEvent("console-aller", { detail: { onglet: "reglages", id: "sauvegarde" } })), ms));
}

const MESSAGES_DRIVE = {
  ok: { titre: "Google Drive connecté", texte: "Lance une première sauvegarde avec « Sauvegarder maintenant ». Ensuite, elle part toute seule chaque nuit (la date de la prochaine est affichée sous le bouton)." },
  refus: { titre: "Accès au Drive non accordé", ton: "danger", texte: "Sur l'écran Google, la case « fichiers créés par cette application » doit rester cochée. Recommence avec « Connecter Google Drive »." },
  erreur: { titre: "Connexion à Google Drive impossible", ton: "danger", texte: "Google n'a pas confirmé la connexion. Recommence avec « Connecter Google Drive »." },
};

const dateHeure = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-CH")} à ${d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}`;
};
const enMo = (octets) => `${((octets || 0) / 1048576).toFixed(1)} Mo`;

/* Sauvegarde automatique : la console dépose elle-même, chaque nuit, une copie chiffrée de
   toute la base dans un dossier du Google Drive de l'école. */
function SauvegardeAuto({ informer }) {
  const [e, setE] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);
  const charger = useCallback(async () => {
    try { setE(await api("/sauvegarde/etat")); setErreur(""); } catch (x) { setErreur(x.message); }
  }, []);
  useEffect(() => { charger(); }, [charger]);
  // Une sauvegarde en cours : on relit l'état jusqu'à ce qu'elle soit finie.
  useEffect(() => {
    if (!e?.en_cours) return undefined;
    const tic = setInterval(charger, 5000);
    return () => clearInterval(tic);
  }, [e?.en_cours, charger]);
  // Retour de Google : un message, puis on retire ?drive=… de l'adresse.
  useEffect(() => {
    const r = retourDrive();
    if (!r) return;
    window.history.replaceState(null, "", window.location.pathname);
    informer(MESSAGES_DRIVE[r] || MESSAGES_DRIVE.erreur);
  }, [informer]);

  const connecter = async () => {
    setOccupe(true);
    try {
      const r = await api("/sauvegarde/google/lien", { method: "POST", body: JSON.stringify({ retour: window.location.origin }) });
      window.location.href = r.url;
    } catch (x) {
      setOccupe(false);
      await informer({ titre: "Connexion impossible", ton: "danger", texte: x.message });
    }
  };
  const maintenant = async () => {
    setOccupe(true);
    try {
      setE(await api("/sauvegarde/maintenant", { method: "POST" }));
    } catch (x) {
      await informer({ titre: "Sauvegarde impossible", ton: "danger", texte: x.message });
    }
    setOccupe(false);
  };

  if (erreur && !e) return <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>;
  if (!e) return <p className="text-sm text-[var(--muted)]">Chargement…</p>;
  const ok = e.derniere_reussie;
  const echec = e.derniere && !e.derniere.ok ? e.derniere : null;
  return (
    <div className="bg-white rounded-2xl p-5 border space-y-3" style={{ borderColor: "var(--border)" }} data-testid="sauvegarde-auto">
      {!e.cle_dediee && (
        <div className="rounded-xl px-3 py-2 text-xs" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#B45309" }}
          data-testid="sauvegarde-auto-cle">
          Ajoute SAUVEGARDE_CLE sur Render et note-la dans ton gestionnaire de mots de passe :
          sans elle, une sauvegarde ne se relit pas.
        </div>
      )}
      <div className="text-sm space-y-1" data-testid="sauvegarde-auto-etat">
        {e.connecte ? (
          <>
            <p>Drive connecté : <strong>{e.compte || "compte Google"}</strong></p>
            {e.dossier && (
              <p>Dossier : <a href={e.dossier.lien} target="_blank" rel="noreferrer" className="underline">{e.dossier.nom}</a></p>
            )}
            <p>Dernière sauvegarde : {ok ? `${dateHeure(ok.le)} · ${enMo(ok.taille)}` : "pas encore"}
              {e.en_cours && " · une sauvegarde est en cours…"}</p>
            {ok?.verification && (
              <p style={{ color: "#15803D" }}>
                ✓ Relue en entier avant l'envoi ({ok.verification.documents.toLocaleString("fr-CH")} documents,{" "}
                {ok.verification.collections} collections) et identique sur le Drive : elle se recharge telle quelle.
              </p>
            )}
            {echec && (
              <p style={{ color: "#B91C1C" }}>Dernier essai le {dateHeure(echec.le)} : {echec.erreur}</p>
            )}
            {e.prochaine && <p className="text-[var(--muted)]">Prochaine : {dateHeure(e.prochaine)}</p>}
            {e.connecte && !e.prochaine && (
              <p className="text-[var(--muted)]">Version de test : pas de sauvegarde de nuit (à activer avec SAUVEGARDE_EN_TEST=true dans Render). « Sauvegarder maintenant » fonctionne.</p>
            )}
          </>
        ) : (
          <p>{e.configure ? "Google Drive n'est pas connecté : aucune copie ne part la nuit."
            : "Connexion Google pas configurée sur le serveur (variables GOOGLE_OAUTH_…)."}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <BoutonSombre onClick={connecter} disabled={occupe || !e.configure} testid="sauvegarde-auto-connecter">
          <CloudUpload size={14} /> {e.connecte ? "Reconnecter" : "Connecter Google Drive"}
        </BoutonSombre>
        {e.connecte && (
          <BoutonSombre onClick={maintenant} disabled={occupe || e.en_cours} testid="sauvegarde-auto-maintenant">
            <CloudUpload size={14} /> {e.en_cours ? "Sauvegarde en cours…" : "Sauvegarder maintenant"}
          </BoutonSombre>
        )}
      </div>
      <p className="text-[11px] text-[var(--muted)]">
        Connecte-toi avec info1000arts@gmail.com. La console ne voit que ses propres fichiers sur le Drive.
        Tu peux ranger son dossier dans « Sauvegarde Console » : elle le retrouve. Pour restaurer :
        déchiffrer le fichier avec le script restaurer_sauvegarde.py (clé SAUVEGARDE_CLE), puis
        « Importer une sauvegarde » ci-dessous.
      </p>
    </div>
  );
}

/* Sauvegarde : un seul fichier, à ranger sur l'ordinateur de l'école. */
/* Le mode d'emploi en PDF, fabriqué à chaque téléchargement à partir du guide de l'aide
   (manuel_pdf.py) : il dit toujours la même chose que l'assistant de la console. Les
   « Listes de la semaine » ont été retirées le 10.10.2026 : l'écran Liste imprime déjà
   les feuilles de présence (par cours, par jour, ou celles qui ont changé). */
function Manuel({ informer }) {
  const [enCours, setEnCours] = useState("");
  const obtenir = async (quoi, chemin, nom) => {
    setEnCours(quoi);
    try {
      await telecharger(chemin, nom);
    } catch (e) {
      await informer({ titre: "Document indisponible", ton: "danger", texte: e.message });
    }
    setEnCours("");
  };
  const jour = new Date().toISOString().slice(0, 10);
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="manuel">
      <div className="flex flex-wrap gap-3">
        <BoutonSombre disabled={!!enCours} testid="manuel-telecharger"
          onClick={() => obtenir("manuel", "/manuel/pdf", `1000arts-manuel-console-${jour}.pdf`)}>
          <Download size={14} /> {enCours === "manuel" ? "Préparation…" : "Manuel de la console (PDF)"}
        </BoutonSombre>
      </div>
      <p className="text-[11px] text-[var(--muted)] mt-3">
        Le même contenu que l'assistant « Aide » : chaque écran, ce que la console fait toute
        seule, et « que faire si… ». Il est refait à chaque téléchargement, donc toujours à jour.
      </p>
    </div>
  );
}

function Sauvegarde({ informer, demander }) {
  const [enCours, setEnCours] = useState(false);
  const [import_, setImport] = useState(false);
  const importer = async (evt) => {
    const fichier = evt.target.files?.[0];
    evt.target.value = "";
    if (!fichier) return;
    if (!await demander({
      titre: "Importer cette sauvegarde",
      texte: `Fichier : ${fichier.name}. Toutes les données de la console seront remplacées par celles du fichier.`,
      points: ["À faire seulement sur une console neuve, ou le soir de la bascule.",
        "Le compte admin de ce serveur garde son mot de passe."],
      valider: "Remplacer et importer",
    })) return;
    setImport(true);
    try {
      const corps = new FormData();
      corps.append("fichier", fichier);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/console/migration/import?remplacer=true&confirmation=REMPLACER`,
        { method: "POST", headers: { Authorization: `Bearer ${jeton()}` }, body: corps });
      const r = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(r.detail || `Erreur ${res.status}`);
      const lignes = (r.rapport || []).filter((l) => l.dans_le_fichier)
        .map((l) => `${l.collection} : ${l["après"]}`);
      // Les restes des familles fictives (audition fictive, comptes de test…) sont retirés
      // juste après l'import : on dit combien.
      const fictives = Object.values(r.fictives_retirees || {}).reduce((t, n) => t + n, 0);
      // Le contrôle de l'import est fait par le serveur dans la foulée : son verdict d'abord.
      const controle = r.controle || {};
      window.dispatchEvent(new CustomEvent("controle-import-maj"));
      await informer({
        titre: "Sauvegarde importée",
        ton: controle.statut === "ok" ? "succes" : controle.statut ? "attention" : undefined,
        texte: `Sauvegarde du ${String(r.sauvegarde_du || "").slice(0, 16).replace("T", " ")}.`
          + (controle.resume ? `\nContrôle de l'import : ${controle.resume}` : "")
          + (fictives ? `\n${fictives} donnée(s) fictive(s) de la version de test retirée(s) (familles de test, audition fictive, comptes de test).` : "")
          + "\nLe détail est dans « Contrôle de l'import », juste en dessous.",
        points: lignes.slice(0, 12),
      });
    } catch (e) {
      await informer({ titre: "Import impossible", ton: "danger", texte: e.message });
    }
    setImport(false);
  };
  const [enCoursComplet, setEnCoursComplet] = useState(false);
  const telecharger = async () => {
    setEnCours(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/console/migration/export`,
        { headers: { Authorization: `Bearer ${jeton()}` } });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const lien = document.createElement("a");
      lien.href = URL.createObjectURL(blob);
      lien.download = `console-1000arts-${new Date().toISOString().slice(0, 10)}.json.gz`;
      lien.click();
      URL.revokeObjectURL(lien.href);
    } catch (e) {
      await informer({ titre: "Sauvegarde impossible", ton: "danger", texte: e.message });
    }
    setEnCours(false);
  };
  // Sauvegarde complète : toutes les collections de la base, types MongoDB conservés,
  // pour recopier un environnement à l'identique.
  const telechargerComplet = async () => {
    setEnCoursComplet(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/console/migration/export-complet`,
        { headers: { Authorization: `Bearer ${jeton()}` } });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const lien = document.createElement("a");
      lien.href = URL.createObjectURL(blob);
      lien.download = `console-1000arts-COMPLET-${new Date().toISOString().slice(0, 10)}.json.gz`;
      lien.click();
      URL.revokeObjectURL(lien.href);
    } catch (e) {
      await informer({ titre: "Sauvegarde impossible", ton: "danger", texte: e.message });
    }
    setEnCoursComplet(false);
  };
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="sauvegarde">
      <div className="flex flex-wrap gap-3">
        <BoutonSombre onClick={telecharger} disabled={enCours} testid="sauvegarde-telecharger">
          <Download size={14} /> {enCours ? "Préparation…" : "Télécharger la sauvegarde"}
        </BoutonSombre>
        <BoutonSombre onClick={telechargerComplet} disabled={enCoursComplet} testid="sauvegarde-complete-telecharger">
          <Download size={14} /> {enCoursComplet ? "Préparation…" : "Sauvegarde complète (migration)"}
        </BoutonSombre>
        {/* L'import REMPLACE toute la console : depuis la bascule, il ne s'affiche plus qu'en
            mode avancé (« ?avance=1 »), comme le retour au mode test (audit du 10.10.2026). */}
        {modeAvance() && (
          <label className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border cursor-pointer"
            style={{ borderColor: "var(--border)", opacity: import_ ? 0.5 : 1 }} data-testid="sauvegarde-importer">
            <Upload size={14} /> {import_ ? "Import en cours…" : "Importer une sauvegarde"}
            <input type="file" accept=".gz,.json" className="hidden" disabled={import_} onChange={importer} />
          </label>
        )}
      </div>
      <p className="text-[11px] text-[var(--muted)] mt-3">
        Les factures PDF déjà envoyées ne sont pas dans ce fichier : elles se retrouvent dans
        l'écran « Factures envoyées ».
      </p>
    </div>
  );
}

/* Interrupteur des e-mails. Le passage en réel se confirme : à partir de ce clic, les
   familles reçoivent vraiment factures, rappels et réponses. */
function ModeEmails({ reel, copie, peutChanger = true, onChange, demander, informer }) {
  const [enCours, setEnCours] = useState(false);
  const basculer = async (versReel) => {
    if (enCours) return;
    setEnCours(true);
    try {
      await appliquer(versReel);
    } finally {
      setEnCours(false);
    }
  };
  // Le changement de mode est verrouillé pendant la question de confirmation : un
  // deuxième clic activait puis désactivait aussitôt le mode réel (vu le 21.09 à 13:05).
  const appliquer = async (versReel) => {
    if (versReel && !await demander({
      titre: "Passer les e-mails en mode réel",
      ton: "danger",
      texte: "À partir de maintenant, chaque envoi part à la vraie adresse de la famille.",
      points: ["Factures, rappels, attestations, réponses aux messages, places de spectacle.",
        "Rien ne part tout seul : un e-mail ne quitte la console que sur ton clic."],
      valider: "Activer le mode réel",
      annuler: "Rester en mode test",
    })) return;
    if (!versReel && !await demander({
      titre: "Revenir en mode test",
      texte: "Plus aucun e-mail ne partira : ni aux familles, ni sur une adresse de test. "
        + "Les envois seront seulement inscrits au journal.",
      valider: "Revenir en mode test",
    })) return;
    try {
      const r = await api("/reglages/emails-mode", { method: "POST", body: JSON.stringify({ reel: versReel }) });
      await onChange();
      await informer({
        titre: r.reel ? "Mode réel activé" : "Mode test activé",
        ton: r.reel ? "attention" : "succes",
        texte: r.reel ? "Les prochains envois partent aux familles."
          : "Les prochains envois ne partiront nulle part : ils seront seulement inscrits au journal.",
      });
    } catch (e) {
      await informer({ titre: "Changement impossible", ton: "danger", texte: e.message });
    }
  };
  /* L'école est en mode réel depuis la reprise : afficher le témoin et l'explication du
     mode test ne lui apprenait plus rien (demandé le 02.10.2026). Le mode test reste
     visible et basculable en mode avancé — et, par sécurité, le témoin réapparaît si
     quelqu'un y repasse. */
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="mode-emails">
      {(!reel || modeAvance()) && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-xs font-semibold px-3 py-1 rounded-full" data-testid="mode-emails-etat"
            style={reel
              ? { background: "#DCFCE7", color: "#15803D", border: "1px solid #BBF7D0" }
              : { background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>
            {reel ? "MODE RÉEL — les familles reçoivent les e-mails" : "MODE TEST — rien ne part aux familles"}
          </span>
          {/* Réservé aux comptes administrateurs : un assistant voit le mode, sans le bouton. */}
          {modeAvance() && peutChanger && (
            <BoutonSombre onClick={() => basculer(!reel)} disabled={enCours} testid="mode-emails-basculer">
              {reel ? "Repasser en mode test" : "Passer en mode réel"}
            </BoutonSombre>
          )}
          {modeAvance() && !peutChanger && (
            <span className="text-xs text-[var(--muted)]" data-testid="mode-emails-reserve">
              Changer de mode est réservé à un compte administrateur.
            </span>
          )}
        </div>
      )}
      <CopieEcole valeur={copie} onChange={onChange} informer={informer} />
    </div>
  );
}

/* L'adresse qui reçoit une copie des factures envoyées aux familles.
   Vide = personne en copie. La copie ne part qu'en mode réel : un essai en aperçu ne doit
   rien coûter en e-mails. */
function CopieEcole({ valeur, onChange, informer }) {
  const [texte, setTexte] = useState(valeur || "");
  const [enCours, setEnCours] = useState(false);
  const modifie = (texte || "").trim() !== (valeur || "").trim();

  const enregistrer = async () => {
    setEnCours(true);
    try {
      const r = await api("/reglages/emails-copie", {
        method: "POST", body: JSON.stringify({ adresses: texte }),
      });
      await onChange();
      await informer({
        titre: r.adresses ? "Copie activée" : "Copie désactivée",
        ton: "succes",
        texte: r.adresses
          ? `Chaque facture ET chaque rappel envoyés à une famille partiront aussi en copie à ${r.adresses}.`
          : "Plus personne ne reçoit de copie des factures ni des rappels.",
      });
    } catch (e) {
      await informer({ titre: "Enregistrement impossible", ton: "danger", texte: e.message });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--border)" }}
      data-testid="copie-ecole">
      <div className="text-xs font-semibold">Copie des factures et des rappels à l'école</div>
      <p className="text-[11px] text-[var(--muted)] mt-1">
        Cette adresse reçoit une copie de chaque facture <strong>et de chaque rappel</strong>
        {" "}envoyés à une famille, pièce jointe comprise. Plusieurs adresses : sépare-les par
        une virgule. Laisse vide pour ne mettre personne en copie.
      </p>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input value={texte} onChange={(e) => setTexte(e.target.value)} type="text"
          placeholder="info1000arts@gmail.com" data-testid="copie-ecole-champ"
          className="flex-1 min-w-[220px] text-sm rounded-xl px-3 py-2 border"
          style={{ borderColor: "var(--border)" }} />
        <BoutonSombre onClick={enregistrer} disabled={enCours || !modifie}
          testid="copie-ecole-enregistrer">
          {enCours ? "Enregistrement…" : "Enregistrer"}
        </BoutonSombre>
      </div>
      {valeur ? (
        <p className="text-[11px] mt-2" style={{ color: "#15803D" }}
          data-testid="copie-ecole-etat">
          Copie active vers {valeur}
        </p>
      ) : (
        <p className="text-[11px] mt-2 text-[var(--muted)]" data-testid="copie-ecole-etat">
          Aucune copie : les factures restent consultables dans Facturation → « Voir la facture ».
        </p>
      )}
    </div>
  );
}

/* Jour de bascule du prorata : avant ce jour le mois de l'inscription est facturé en entier,
   à partir de ce jour il compte pour moitié (décision de l'école, 24.09.2026). Le semestre
   vaut 5 mois (septembre → janvier, février → juin). */
const NOMS_MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
  "août", "septembre", "octobre", "novembre", "décembre"];

/* Les mois facturés d'un semestre, dans l'ordre, d'après les réglages. */
function moisDuSemestre(semestres, semestre) {
  const s = semestres?.[String(semestre)] || (semestre === 1
    ? { mois_debut: 9, mois_fin: 1 } : { mois_debut: 2, mois_fin: 6 });
  const mois = [];
  let m = Number(s.mois_debut) || (semestre === 1 ? 9 : 2);
  const fin = Number(s.mois_fin) || (semestre === 1 ? 1 : 6);
  for (let i = 0; i < 12; i += 1) {
    mois.push(m);
    if (m === fin) break;
    m = m === 12 ? 1 : m + 1;
  }
  return mois;
}

const demi = (x) => `${x}`.replace(".5", "½").replace(/^0½$/, "½");

/* Le tableau de lecture : pour un cours à 390 CHF, ce que chaque date d'inscription
   déduit. C'est la question que l'école se pose vraiment (« et mi-octobre ? »), donc
   on l'affiche au lieu de la faire recalculer à la main. */
function TableProrata({ semestre, semestres, jour, prix, nbMois }) {
  const mois = moisDuSemestre(semestres, semestre);
  const rangs = [];
  mois.forEach((m, index) => {
    [false, true].forEach((tard) => {
      const perdus = index + (tard ? 0.5 : 0);
      if (perdus >= nbMois) return;
      rangs.push({
        quand: `${tard ? `dès le ${jour}` : `avant le ${jour}`} ${NOMS_MOIS[m - 1]}`,
        perdus,
        deduit: Math.round((prix * perdus) / nbMois * 100) / 100,
      });
    });
  });
  return (
    <table className="w-full text-[11px] mt-2" data-testid={`prorata-table-s${semestre}`}>
      <thead>
        <tr className="text-left text-[var(--muted)]">
          <th className="py-1 font-medium">Inscription</th>
          <th className="py-1 font-medium text-right">Mois dus</th>
          <th className="py-1 font-medium text-right">Déduit</th>
          <th className="py-1 font-medium text-right">À payer</th>
        </tr>
      </thead>
      <tbody>
        {rangs.map((r) => (
          <tr key={r.quand} className="border-t" style={{ borderColor: "var(--border)" }}>
            <td className="py-1">{r.quand}</td>
            <td className="py-1 text-right tabular-nums">{demi(nbMois - r.perdus)} / {nbMois}</td>
            <td className="py-1 text-right tabular-nums">
              {r.deduit ? `− ${r.deduit.toFixed(0)}` : "—"}
            </td>
            <td className="py-1 text-right tabular-nums font-medium">
              {(prix - r.deduit).toFixed(0)} CHF
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Prorata({ jour, semestres, onEnregistrer }) {
  const [n, setN] = useState(jour);
  const s1 = semestres?.["1"] || { mois_debut: 9, mois_fin: 1 };
  const nbMois = ((s1.mois_fin - s1.mois_debut + 12) % 12) + 1;
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="prorata-bloc">
      <Eyebrow>Jour de bascule</Eyebrow>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <span className="text-xs text-[var(--muted)]">Inscription à partir du</span>
        <input
          className="input-field"
          style={{ width: 80, paddingTop: 8, paddingBottom: 8, textAlign: "right" }}
          type="number" min="1" max="28"
          value={n}
          onChange={(e) => setN(e.target.value)}
          data-testid="prorata-jour"
        />
        <span className="text-xs text-[var(--muted)]">du mois → le mois de l'inscription compte pour moitié.</span>
        <BoutonSombre onClick={() => onEnregistrer(Math.min(28, Math.max(1, Number(n) || 15)))}
          testid="prorata-enregistrer" disabled={String(n) === String(jour)}>
          Enregistrer
        </BoutonSombre>
      </div>
      <div className="rounded-2xl px-4 py-3 text-xs mt-4" style={{ background: "var(--primary-tint)" }} data-testid="prorata-explication">
        <strong>Comment c'est calculé</strong>
        <ul className="mt-2 space-y-1 text-[var(--muted)]">
          <li>· Le semestre vaut {nbMois} mois facturés (semestre 1 : septembre → janvier · semestre 2 : février → juin).</li>
          <li>· On déduit les mois entièrement passés, plus <strong>un demi-mois</strong> si l'inscription tombe le {jour} ou après.</li>
          <li>· Déduction = prix du cours ÷ {nbMois} × nombre de mois non facturés.</li>
          <li>· La déduction porte sur le <strong>prix du cours</strong> seul : la taxe
            administrative et le costume de spectacle ne sont jamais proratés.</li>
        </ul>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <div>
            <Eyebrow>Semestre 1 — cours à 390 CHF</Eyebrow>
            <TableProrata semestre={1} semestres={semestres} jour={jour} prix={390} nbMois={nbMois} />
          </div>
          <div>
            <Eyebrow>Semestre 2 — cours à 390 CHF</Eyebrow>
            <TableProrata semestre={2} semestres={semestres} jour={jour} prix={390} nbMois={nbMois} />
          </div>
        </div>
        <p className="mt-3 text-[var(--muted)]">
          Le semestre 2 suit exactement la même règle, sur février → juin : une inscription
          de septembre n'y donne aucun prorata (le semestre n'a pas commencé), mais une
          arrivée en cours d'année, oui.
        </p>
        <p className="mt-2 text-[var(--muted)]">
          Le {jour} correspond à la 3ᵉ semaine du mois : l'inscription ne démarre vraiment qu'une
          semaine plus tard (après l'essai), la moitié du mois est alors considérée comme perdue.
        </p>
        <p className="mt-2" style={{ color: "#B45309" }}>
          Les factures déjà envoyées ne bougent pas. Les prorata déjà décidés (annoncés à la
          famille) sont conservés tels quels.
        </p>
      </div>
    </div>
  );
}

/* Quelles disciplines sont encaissées par Noémie, et les stages avec ou sans elle.
   L'école ajoute une discipline en un clic, sans passer par nous. */
/* Les professeurs : prénom affiché dans le planning + nom complet pour les attestations.
   L'école ajoute ou retire un prof sans passer par nous. */
function Professeurs({ liste, complets, onEnregistrer }) {
  const [rangs, setRangs] = useState(
    liste.map((p) => ({ prenom: p, complet: complets[p] || "" })));
  const [nouveau, setNouveau] = useState({ prenom: "", complet: "" });
  const enregistre = (rows, message) => {
    const propres = rows.filter((r) => r.prenom.trim());
    onEnregistrer({
      listes: { professeurs: propres.map((r) => r.prenom.trim()) },
      profs_noms_complets: Object.fromEntries(
        propres.filter((r) => r.complet.trim()).map((r) => [r.prenom.trim(), r.complet.trim()])),
    }, message);
  };
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="profs-bloc">
      <div className="space-y-2">
        {rangs.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input value={r.prenom} data-testid={`prof-prenom-${i}`}
              onChange={(e) => setRangs(rangs.map((x, k) => k === i ? { ...x, prenom: e.target.value } : x))}
              placeholder="Prénom (planning)"
              className="px-3 py-2 rounded-xl text-sm border w-[190px]" style={{ borderColor: "var(--border)" }} />
            <input value={r.complet} data-testid={`prof-complet-${i}`}
              onChange={(e) => setRangs(rangs.map((x, k) => k === i ? { ...x, complet: e.target.value } : x))}
              placeholder="Nom complet (attestations)"
              className="px-3 py-2 rounded-xl text-sm border w-[260px]" style={{ borderColor: "var(--border)" }} />
            <button className="btn-ghost" data-testid={`prof-retirer-${i}`}
              onClick={() => { const rows = rangs.filter((_, k) => k !== i); setRangs(rows); enregistre(rows, `${r.prenom} retiré de la liste.`); }}>
              Retirer
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
        <input value={nouveau.prenom} onChange={(e) => setNouveau({ ...nouveau, prenom: e.target.value })}
          placeholder="Prénom (planning)" data-testid="prof-nouveau-prenom"
          className="px-3 py-2 rounded-xl text-sm border w-[190px]" style={{ borderColor: "var(--border)" }} />
        <input value={nouveau.complet} onChange={(e) => setNouveau({ ...nouveau, complet: e.target.value })}
          placeholder="Nom complet (attestations)" data-testid="prof-nouveau-complet"
          className="px-3 py-2 rounded-xl text-sm border w-[260px]" style={{ borderColor: "var(--border)" }} />
        <BoutonSombre testid="prof-ajouter" disabled={!nouveau.prenom.trim()}
          onClick={() => {
            const rows = [...rangs, nouveau];
            setRangs(rows); setNouveau({ prenom: "", complet: "" });
            enregistre(rows, `${nouveau.prenom} ajouté : coche-le maintenant sur ses cours dans le planning.`);
          }}>
          Ajouter un professeur
        </BoutonSombre>
        <BoutonSombre testid="profs-enregistrer" onClick={() => enregistre(rangs, "Liste des professeurs enregistrée.")}>
          Enregistrer les modifications
        </BoutonSombre>
      </div>
      <p className="text-xs text-[var(--muted)] mt-3">
        Le prénom sert partout dans la console (planning, feuilles de présence, filtres du site).
        Le nom complet n'apparaît que sur les attestations d'assurance.
      </p>
    </div>
  );
}

function BvNoemie({ disciplines, toutes, stages, onEnregistrer }) {
  const [choix, setChoix] = useState(disciplines);
  const liste = [...new Set([...toutes, ...disciplines])];
  const change = JSON.stringify([...choix].sort()) !== JSON.stringify([...disciplines].sort());
  const basculer = (d) => setChoix(choix.includes(d) ? choix.filter((x) => x !== d) : [...choix, d]);
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="bv-noemie-bloc">
      <Eyebrow>Disciplines encaissées par Noémie</Eyebrow>
      <div className="flex flex-wrap gap-2 mt-3">
        {liste.map((d) => (
          <button key={d} onClick={() => basculer(d)} data-testid={`bv-noemie-${d}`}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
            style={choix.includes(d)
              ? { background: "#0F1B2D", color: "#fff", borderColor: "#0F1B2D" }
              : { background: "#fff", color: "var(--muted)", borderColor: "var(--border)" }}>
            {d}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <BoutonSombre onClick={() => onEnregistrer({ disciplines_bv_noemie: choix },
          choix.length ? `Encaissé par Noémie : ${choix.join(", ")}.` : "Plus aucune discipline sur le bulletin de Noémie.")}
          testid="bv-noemie-enregistrer" disabled={!change}>
          Enregistrer
        </BoutonSombre>
        <span className="text-xs text-[var(--muted)]">
          {choix.length ? `${choix.length} discipline(s) sur son bulletin` : "toutes les factures partent sur le compte de l'école"}
        </span>
      </div>
      <div className="rounded-2xl px-4 py-3 mt-4 flex flex-wrap items-center gap-3" style={{ background: "var(--primary-tint)" }}>
        <span className="text-xs">Les <strong>factures de stage</strong> sont encaissées par Noémie</span>
        <button onClick={() => onEnregistrer({ stages_bv_noemie: !stages },
          !stages ? "Les prochaines factures de stage porteront le bulletin de Noémie."
            : "Les prochaines factures de stage repartent sur le compte de l'école.")}
          data-testid="bv-noemie-stages"
          className="px-3 py-1.5 rounded-full text-xs font-semibold border"
          style={stages ? { background: "#0F1B2D", color: "#fff", borderColor: "#0F1B2D" }
            : { background: "#fff", color: "var(--muted)", borderColor: "var(--border)" }}>
          {stages ? "Oui" : "Non"}
        </button>
        <span className="text-[11px] text-[var(--muted)]">
          Les factures déjà envoyées ne changent pas de compte.
        </span>
      </div>
    </div>
  );
}

/* Délai de paiement (l'échéance d'une facture) et délai minimum entre deux rappels à la
   même famille. La console ne relance qu'après l'un, et jamais avant la fin de l'autre. */
function DelaisRelances({ paiement, entre, onEnregistrer }) {
  const [p, setP] = useState(String(paiement ?? 30));
  const [e, setE] = useState(String(entre ?? 14));
  const change = p !== String(paiement ?? 30) || e !== String(entre ?? 14);
  return (
    <div className="mt-2 space-y-2">
      <label className="flex flex-wrap items-center gap-3 text-xs text-[var(--muted)]">
        <input className="input-field" style={{ width: 90, paddingTop: 8, paddingBottom: 8 }}
          type="number" min={1} max={120} value={p} onChange={(ev) => setP(ev.target.value)}
          data-testid="reglages-delai-paiement" />
        jours pour payer après l'envoi de la facture (l'échéance) — aucun rappel avant.
      </label>
      <label className="flex flex-wrap items-center gap-3 text-xs text-[var(--muted)]">
        <input className="input-field" style={{ width: 90, paddingTop: 8, paddingBottom: 8 }}
          type="number" min={1} max={120} value={e} onChange={(ev) => setE(ev.target.value)}
          data-testid="reglages-delai-relances" />
        jours au moins entre deux rappels à la même famille.
      </label>
      <p className="text-[11px] text-[var(--muted)]">
        Jamais de rappel à un paiement échelonné à jour, aux espèces ni à une facture à corriger.
      </p>
      <BoutonSombre testid="reglages-relances-enregistrer" disabled={!change}
        onClick={() => onEnregistrer({ delai_paiement_jours: Number(p) || 30,
          delai_entre_relances_jours: Number(e) || 14 })}>
        Enregistrer
      </BoutonSombre>
    </div>
  );
}

function SeuilPlaces({ valeur, onEnregistrer }) {
  const [n, setN] = useState(valeur);
  return (
    <div className="flex items-center gap-3 mt-2">
      <input
        className="input-field"
        style={{ width: 90, paddingTop: 8, paddingBottom: 8 }}
        type="number"
        value={n}
        onChange={(e) => setN(e.target.value)}
        data-testid="reglages-seuil"
      />
      <span className="text-xs text-[var(--muted)]">
        place(s) libre(s) ou moins → le cours annonce « dernières places » sur le site.
      </span>
      <BoutonSombre onClick={() => onEnregistrer(Number(n) || 0)} testid="reglages-seuil-enregistrer" disabled={String(n) === String(valeur)}>
        Enregistrer
      </BoutonSombre>
    </div>
  );
}

/* Petite table de valeurs numériques modifiables (tarifs, paramètres). Une ligne peut être
   ajoutée : une nouvelle durée de cours, un nouveau montant de règlement. */
function TableNombres({ titre, valeurs, suffixe, legende, libelles = {}, testid, onEnregistrer,
                        ajout = "" }) {
  const [v, setV] = useState(valeurs);
  const [nouveau, setNouveau] = useState(null);
  const modifie = JSON.stringify(v) !== JSON.stringify(valeurs);
  const ajouter = () => {
    const cle = String(nouveau?.cle || "").trim();
    if (!cle) return;
    setV({ ...v, [cle]: Number(nouveau.valeur) || 0 });
    setNouveau(null);
  };
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <Eyebrow>{titre}</Eyebrow>
      <div className="mt-3 space-y-2">
        {Object.entries(v).map(([cle, val]) => (
          <div key={cle} className="flex items-center gap-3">
            <span className="text-xs text-[var(--muted)] flex-1">{libelles[cle] || `${cle} ${legende || ""}`}</span>
            <input
              className="input-field"
              style={{ width: 96, paddingTop: 6, paddingBottom: 6, textAlign: "right" }}
              type="number"
              value={val}
              onChange={(e) => setV({ ...v, [cle]: Number(e.target.value) })}
              data-testid={`${testid}-${cle}`}
            />
            <span className="text-[11px] text-[var(--muted)] w-8">{suffixe}</span>
          </div>
        ))}
      </div>
      {nouveau && (
        <div className="flex items-center gap-3 mt-3" data-testid={`${testid}-nouveau`}>
          <input className="input-field" style={{ flex: 1, paddingTop: 6, paddingBottom: 6 }}
            placeholder={ajout || "nom de la ligne"} value={nouveau.cle}
            data-testid={`${testid}-nouveau-cle`}
            onChange={(e) => setNouveau({ ...nouveau, cle: e.target.value })} />
          <input className="input-field" style={{ width: 96, paddingTop: 6, paddingBottom: 6, textAlign: "right" }}
            type="number" value={nouveau.valeur} data-testid={`${testid}-nouveau-valeur`}
            onChange={(e) => setNouveau({ ...nouveau, valeur: e.target.value })} />
          <span className="text-[11px] text-[var(--muted)] w-8">{suffixe}</span>
        </div>
      )}
      <div className="mt-4 flex items-center gap-2">
        <BoutonSombre onClick={() => onEnregistrer(v)} testid={`${testid}-enregistrer`} disabled={!modifie}>Enregistrer</BoutonSombre>
        {nouveau
          ? (
            <>
              <button className="btn-ghost" onClick={ajouter} data-testid={`${testid}-ajouter-valider`}>
                Ajouter la ligne
              </button>
              <button className="btn-ghost" onClick={() => setNouveau(null)} data-testid={`${testid}-ajouter-annuler`}>
                Annuler
              </button>
            </>
          )
          : (
            <button className="btn-ghost" onClick={() => setNouveau({ cle: "", valeur: 0 })}
              data-testid={`${testid}-ajouter`}>
              + Ajouter une ligne
            </button>
          )}
      </div>
      <p className="text-[11px] text-[var(--muted)] mt-2">
        Une ligne ajoutée ou modifiée ne vaut que pour les prochains calculs : les factures déjà
        envoyées gardent leur montant, l'historique n'est jamais réécrit.
      </p>
    </div>
  );
}

/* Table de valeurs texte modifiables (codes TAS par discipline, dates de saison). */
function TableTexte({ valeurs, libelles = {}, exclure = [], placeholder, testid, onEnregistrer }) {
  const depart = Object.fromEntries(Object.entries(valeurs).filter(([k]) => !exclure.includes(k)));
  const [v, setV] = useState(depart);
  const modifie = JSON.stringify(v) !== JSON.stringify(depart);
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
        {Object.entries(v).map(([cle, val]) => (
          <div key={cle} className="flex items-center gap-3">
            <span className="text-xs text-[var(--muted)] flex-1 truncate">{libelles[cle] || cle}</span>
            <input
              className="input-field"
              style={{ width: 120, paddingTop: 6, paddingBottom: 6 }}
              value={val}
              placeholder={placeholder}
              onChange={(e) => setV({ ...v, [cle]: e.target.value })}
              data-testid={`${testid}-${cle.toLowerCase().replace(/\s+/g, "-")}`}
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <BoutonSombre onClick={() => onEnregistrer(v)} testid={`${testid}-enregistrer`} disabled={!modifie}>Enregistrer</BoutonSombre>
      </div>
    </div>
  );
}
